// Voice Intelligence — Phase 7 list filtering and export columns.
//
// Pure functions over rows enriched with `getConversationIntelMap` output (see
// api/conversation.js). Kept out of the two 3000-line page components so both
// the admin and client pages share one implementation and it can be tested
// without rendering a page.
//
// Every option value below is a value that actually occurs in the data — taken
// from the Phase 0 probe over 497 voice conversations, not from the spec's
// prose. Notably there is NO `booked` outcome: the booking outcomes are
// `reservation_made` and `reservation_assisted`, so a "booked" option would
// match nothing. See .local/docs/2026-08-24-voice-intelligence-phase0-probe.md.

// Shared with the detail pane, so it lives in shared/format.js. Re-exported here
// because this module's own call sites and tests import it by this name.
export { labelFor } from '../../shared/format';
import { labelFor } from '../../shared/format';

// `unknown` is a real stored value and is offered so a reader can isolate the
// rows the extraction could not classify — it is data, not an absence.
export const OUTCOME_OPTIONS = [
  'abandoned', 'info_provided', 'reservation_assisted', 'transferred_to_human',
  'quoted_no_booking', 'voicemail_left', 'reservation_made', 'wrong_number', 'other',
];

export const STAY_TYPE_OPTIONS = ['nightly', 'monthly', 'day_use', 'extended', 'annual', 'unknown'];

export const URGENCY_OPTIONS = [
  'immediate', 'this_week', 'this_month', 'this_season', 'next_season', 'browsing', 'unknown',
];

// Chat's own vocabularies, from the Phase 0 probe over the 78 chat
// conversations in the default window (2026-08-26). They are NOT the voice lists:
// chat has no voicemail and no wrong number, and it carries two stay types and
// one outcome the voice data never produced. Offering an option that matches
// nothing is a control that appears broken.
export const CHAT_OUTCOME_OPTIONS = [
  'abandoned', 'info_provided', 'reservation_assisted', 'reservation_made',
  'quoted_no_booking', 'complaint_handled', 'transferred_to_human', 'other',
];

export const CHAT_STAY_TYPE_OPTIONS = [
  'nightly', 'monthly', 'seasonal', 'day_use', 'workamper', 'extended', 'annual', 'unknown',
];

// Chat drops the after-hours control: "a website chat at 9pm is not remarkable
// the way an answered phone call is." The filter is not hidden, it does not
// exist on this modality — the control is never mounted.
export const CHAT_FILTER_KEYS = ['outcome', 'stayType', 'urgency', 'needsFollowup', 'hasBarrier'];

export const CALLER_TYPE_OPTIONS = [
  'guest', 'business', 'vendor', 'staff', 'ai_agent', 'wrong_number', 'spam', 'unknown',
];

// caller_type=guest is the plan's default. It is a real filter with a visible
// control set to "guest", NOT a hidden predicate — a reader can see why rows are
// missing and can clear it. Everything else defaults to unfiltered.
export const DEFAULT_INTEL_FILTERS = {
  outcome: 'all',
  stayType: 'all',
  urgency: 'all',
  needsFollowup: 'all',
  hasBarrier: 'all',
  afterHours: 'all',
  callerType: 'guest',
};

export const isDefaultIntelFilters = (f) =>
  Object.keys(DEFAULT_INTEL_FILTERS).every((k) => f?.[k] === DEFAULT_INTEL_FILTERS[k]);

/** Count of filters currently narrowing the list, for the "Clear" affordance. */
export const activeIntelFilterCount = (f) =>
  Object.keys(DEFAULT_INTEL_FILTERS).filter((k) => f?.[k] && f[k] !== 'all').length;

// ─── Rail sort (§E.15 Date / Activity) ──────────────────────────────────────
// Client-side over the fully-loaded rail set. `date` orders by start time,
// `activity` by conversation length: the list rows carry no message/turn count,
// so `duration_seconds` is the honest activity proxy (the v1 page ranked by
// messageCount, a field the new list RPC does not return). Both orders are
// descending — most recent, or most active, first. Pure: copies, never mutates;
// a row missing the sort field sorts last (treated as 0).
export const RAIL_SORTS = ['date', 'activity'];

export const sortRailRows = (rows, sortBy) => {
  const list = Array.isArray(rows) ? rows.slice() : [];
  if (sortBy === 'activity') {
    return list.sort((a, b) => (Number(b?.duration_seconds) || 0) - (Number(a?.duration_seconds) || 0));
  }
  return list.sort((a, b) => (Date.parse(b?.started_at) || 0) - (Date.parse(a?.started_at) || 0));
};

/**
 * The intelligence record merged onto a row, or an empty object.
 * Rows the list RPC did not return are never dropped — an absent record means
 * "not known", so every filter treats them as non-matching rather than assuming.
 */
export const intelOf = (row) => row?.intel || {};

/**
 * Merge one intelligence record onto a row.
 *
 * Also upgrades the row label when the list RPC resolved a person the loader
 * could not: `getTranscripts` falls back to the caller's number or a "Guest …"
 * placeholder, and the plan asks for the person's name once it is known. Only a
 * placeholder is ever replaced — a name the loader already has wins, so this can
 * never overwrite a better label with a worse one.
 */
export const mergeIntel = (row, intel) => {
  if (!intel) return row;
  const merged = { ...row, intel };
  const current = String(row?.displayName || '');
  const isPlaceholder = current === '' || current.startsWith('Guest ') || /^[+(\d]/.test(current);
  const resolved = intel.visitor_name;
  if (isPlaceholder && resolved && String(resolved).trim() && !/^[+(\d]/.test(String(resolved))) {
    merged.displayName = String(resolved).trim();
  }
  return merged;
};

const yesNo = (setting, value) => {
  if (setting === 'all') return true;
  if (value == null) return false;
  return setting === 'yes' ? value === true : value === false;
};

/** After-hours = started outside 09:00–17:00 local time. A filter, not a metric. */
const startedAfterHours = (row) => {
  const ts = intelOf(row).started_at || row?.convo?.started_at || row?.date;
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  const h = d.getHours();
  return h < 9 || h >= 17;
};

/**
 * Apply the six Phase 7 filters plus caller type. Pure: returns a new array and
 * never mutates. Rows lacking the field a filter targets are excluded, so a
 * filter never silently widens to include unknowns.
 */
export const applyIntelFilters = (rows, filters) => {
  const f = { ...DEFAULT_INTEL_FILTERS, ...(filters || {}) };
  let out = Array.isArray(rows) ? rows : [];

  // Enrichment arrives after the list does. Until it lands NO row has an `intel`
  // record, so the default caller_type=guest filter would reject every one of
  // them and show an empty worklist over a full list — and it would stay empty
  // if enrichment ever failed. With nothing to filter on, filter nothing.
  if (!out.some((r) => r?.intel)) return out;

  if (f.outcome !== 'all') out = out.filter((r) => intelOf(r).outcome === f.outcome);
  if (f.stayType !== 'all') out = out.filter((r) => intelOf(r).stay_type_sought === f.stayType);
  if (f.urgency !== 'all') out = out.filter((r) => intelOf(r).booking_urgency === f.urgency);
  if (f.callerType !== 'all') out = out.filter((r) => intelOf(r).caller_type === f.callerType);
  if (f.needsFollowup !== 'all') out = out.filter((r) => yesNo(f.needsFollowup, intelOf(r).requires_followup));
  if (f.hasBarrier !== 'all') {
    out = out.filter((r) => {
      const b = intelOf(r).conversion_barriers;
      if (!Array.isArray(b)) return false;
      return f.hasBarrier === 'yes' ? b.length > 0 : b.length === 0;
    });
  }
  if (f.afterHours !== 'all') {
    out = out.filter((r) => {
      const ah = startedAfterHours(r);
      if (ah == null) return false;
      return f.afterHours === 'yes' ? ah : !ah;
    });
  }
  return out;
};

// ─── Export columns ─────────────────────────────────────────────────────────
// Added to CSV and PDF per the plan: outcome / stay type / urgency / barriers /
// value. Barriers join with "; " because a comma would fight the CSV delimiter
// even when quoted, and reads worse in a PDF cell.
//
// Value is `estimated_lost_value` when the call did not book and
// `reservation_value` when it did — one column, and the outcome column right
// beside it says which it is. Rendered verbatim, never summed or converted.

export const INTEL_EXPORT_HEADERS = ['Outcome', 'Stay type', 'Urgency', 'Barriers', 'Value'];

/**
 * The same five values as `[label, value]` pairs, with absent ones dropped.
 *
 * The PDF exports render one transcript table per conversation rather than one
 * row per conversation, so there is no column to add — the equivalent is a line
 * in each conversation's header block. Empty pairs are omitted rather than
 * printed blank, matching the detail pane's omit-absent rule.
 */
export const intelExportPairs = (row) =>
  intelExportCells(row)
    .map((v, i) => [INTEL_EXPORT_HEADERS[i], v])
    .filter(([, v]) => v !== '');

const BOOKED_OUTCOMES = new Set(['reservation_made', 'reservation_assisted']);

/**
 * Build the intel `extraColumns` for `downloadTranscriptsCsv` from the loaded
 * list rows. The CSV helper queries the `conversations` TABLE (rows keyed by
 * `id`), while the intel fields live on the fn_get_conversation_list rows (keyed
 * by `conversation_id`) — so this joins them by that pair. A table row with no
 * matching list row gets blank intel cells, never another row's, because the
 * lookup misses and `intelExportCells({ intel: {} })` returns all-empty.
 */
export const intelExtraColumns = (listRows) => {
  const byId = new Map(
    (Array.isArray(listRows) ? listRows : []).map((r) => [r.conversation_id, r]),
  );
  return {
    headers: INTEL_EXPORT_HEADERS,
    cellsFor: (tableRow) => intelExportCells({ intel: byId.get(tableRow?.id) || {} }),
  };
};

/** The five export cells for one row, in INTEL_EXPORT_HEADERS order. Absent → ''. */
export const intelExportCells = (row) => {
  const it = intelOf(row);
  const booked = BOOKED_OUTCOMES.has(it.outcome);
  const value = booked ? it.reservation_value : it.estimated_lost_value;
  return [
    it.outcome ? labelFor(it.outcome) : '',
    it.stay_type_sought ? labelFor(it.stay_type_sought) : '',
    it.booking_urgency ? labelFor(it.booking_urgency) : '',
    Array.isArray(it.conversion_barriers) ? it.conversion_barriers.map(labelFor).join('; ') : '',
    value == null || value === 0 ? '' : String(value),
  ];
};
